import { useState } from 'react';
import type { AnalysisResults } from '../../services/api/novel-analysis';

type ParseFn = (str: string | null) => unknown;

export function ScenesTab({ scenes, parseJson }: { scenes: AnalysisResults['sex_scenes']; parseJson: ParseFn }) {
  const [expandedScene, setExpandedScene] = useState<string | null>(null);

  // Group by chapter
  const grouped = scenes.reduce((acc, s) => {
    const key = s.chapter_index;
    if (!acc[key]) acc[key] = [];
    acc[key].push(s);
    return acc;
  }, {} as Record<number, typeof scenes>);

  return (
    <div className="divide-y">
      {Object.entries(grouped).map(([chIdx, chScenes]) => (
        <div key={chIdx} className="p-4">
          <h3 className="font-medium text-sm mb-2">第 {chIdx} 章</h3>
          <div className="space-y-3">
            {chScenes.map((scene) => {
              const isExpanded = expandedScene === scene.id;
              const participants = parseJson(scene.participants) as Array<{ character_name: string; role: string }> || [];
              const sensory = parseJson(scene.sensory_focus) as { visual: number; auditory: number; tactile: number; olfactory: number } | null;
              const positions = parseJson(scene.positions) as string[] | null;
              const techniques = parseJson(scene.techniques_used) as string[] | null;
              const charStates = parseJson(scene.character_states) as Record<string, { before?: string; after?: string }> | null;
              const stages = parseJson(scene.stages) as Array<{ stage_name?: string; position?: string; action?: string; protagonist_state?: string }> | null;
              const orgasmRecords = parseJson(scene.orgasm_records) as Array<{ character?: string; trigger?: string; timing?: string; physical_response?: string; intensity?: number }> | null;
              const cumshotRecords = parseJson(scene.cumshot_records) as Array<{ from_character?: string; location?: string; amount?: string; description?: string }> | null;

              return (
                <div key={scene.id} className="bg-gray-50 rounded p-3">
                  {/* Header */}
                  <button onClick={() => setExpandedScene(isExpanded ? null : scene.id)} className="w-full text-left">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      {scene.scene_name && <span className="font-medium text-sm">{scene.scene_name}</span>}
                      {scene.intensity_level && (
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          scene.intensity_level >= 8 ? 'bg-red-100 text-red-700' :
                          scene.intensity_level >= 5 ? 'bg-orange-100 text-orange-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          強度 {scene.intensity_level}/10
                        </span>
                      )}
                      {scene.location && <span className="text-xs text-gray-500">{scene.location}</span>}
                      {scene.duration && <span className="text-xs text-gray-400">· {scene.duration}</span>}
                      <span className="ml-auto text-gray-400 text-xs">{isExpanded ? '收起' : '展開'}</span>
                    </div>
                  </button>

                  {/* Always visible: participants + description */}
                  {participants.length > 0 && (
                    <p className="text-xs text-gray-600">
                      參與者：{participants.map((p) => `${p.character_name} (${p.role})`).join(', ')}
                    </p>
                  )}
                  {scene.description && <p className="text-sm text-gray-600 mt-1">{scene.description}</p>}

                  {/* Sensory focus */}
                  {sensory && (
                    <div className="flex gap-2 mt-2">
                      {Object.entries(sensory).map(([key, val]) => (
                        <span key={key} className="text-xs text-gray-500">
                          {key === 'visual' ? '視' : key === 'auditory' ? '聽' : key === 'tactile' ? '觸' : '嗅'}: {val}%
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Climax records (always visible) */}
                  {scene.climax_records && scene.climax_records.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {scene.climax_records.map((cr, i) => (
                        <div key={i} className="text-xs text-gray-500">
                          {cr.character_name} - {cr.record_type === 'orgasm' ? '高潮' : '射精'} #{cr.sequence}
                          {cr.intensity && ` (強度 ${cr.intensity}/10)`}
                          {cr.trigger_description && <span className="text-gray-400"> — {cr.trigger_description}</span>}
                          {cr.location_on_body && <span className="text-gray-400"> [{cr.location_on_body}]</span>}
                          {cr.description && <p className="text-gray-400 pl-4">{cr.description}</p>}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Expanded details */}
                  {isExpanded && (
                    <div className="mt-3 pt-3 border-t border-gray-200 space-y-3">
                      {/* Positions */}
                      {positions && positions.length > 0 && (
                        <div>
                          <h5 className="text-xs font-medium text-gray-500 mb-1">體位</h5>
                          <div className="flex gap-1 flex-wrap">
                            {positions.map((p, i) => (
                              <span key={i} className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded text-xs">{p}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Techniques */}
                      {techniques && techniques.length > 0 && (
                        <div>
                          <h5 className="text-xs font-medium text-gray-500 mb-1">寫作技巧</h5>
                          <div className="flex gap-1 flex-wrap">
                            {techniques.map((t, i) => (
                              <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">{t}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Character States */}
                      {charStates && Object.keys(charStates).length > 0 && (
                        <div>
                          <h5 className="text-xs font-medium text-gray-500 mb-1">角色心理變化</h5>
                          {Object.entries(charStates).map(([name, state]) => (
                            <div key={name} className="text-sm mb-1">
                              <span className="font-medium text-gray-700">{name}：</span>
                              <div className="flex items-center gap-1 text-xs text-gray-600 mt-0.5">
                                {state.before && <span className="px-1.5 py-0.5 bg-gray-200 rounded">{state.before}</span>}
                                {state.before && state.after && <span className="text-gray-400">→</span>}
                                {state.after && <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded">{state.after}</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Stages */}
                      {stages && stages.length > 0 && (
                        <div>
                          <h5 className="text-xs font-medium text-gray-500 mb-1">分階段記錄</h5>
                          {stages.map((s, i) => (
                            <div key={i} className="text-sm pl-3 border-l-2 border-pink-200 mb-2">
                              <div className="font-medium text-gray-700">
                                <span className="text-pink-500 mr-1">#{i + 1}</span>
                                {s.stage_name}
                                {s.position && <span className="text-gray-400 text-xs ml-2">[{s.position}]</span>}
                              </div>
                              {s.action && <p className="text-gray-600">{s.action}</p>}
                              {s.protagonist_state && <p className="text-gray-400 text-xs">主角狀態：{s.protagonist_state}</p>}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Orgasm Records */}
                      {orgasmRecords && orgasmRecords.length > 0 && (
                        <div>
                          <h5 className="text-xs font-medium text-gray-500 mb-1">高潮記錄</h5>
                          {orgasmRecords.map((r, i) => (
                            <div key={i} className="text-sm pl-3 border-l-2 border-pink-300 mb-1">
                              <span className="text-pink-600 font-medium">{r.character}</span>
                              {r.intensity && <span className="text-xs text-gray-400 ml-1">(強度 {r.intensity}/10)</span>}
                              {r.trigger && <p className="text-xs text-gray-600">觸發：{r.trigger}</p>}
                              {r.timing && <p className="text-xs text-gray-400">時機：{r.timing}</p>}
                              {r.physical_response && <p className="text-xs text-gray-500">身體反應：{r.physical_response}</p>}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Cumshot Records */}
                      {cumshotRecords && cumshotRecords.length > 0 && (
                        <div>
                          <h5 className="text-xs font-medium text-gray-500 mb-1">射精記錄</h5>
                          {cumshotRecords.map((r, i) => (
                            <div key={i} className="text-sm pl-3 border-l-2 border-purple-300 mb-1">
                              <span className="text-purple-600 font-medium">{r.from_character}</span>
                              {r.location && <span className="text-xs text-gray-500 ml-1">→ {r.location}</span>}
                              {r.amount && <span className="text-xs text-gray-400 ml-1">({r.amount})</span>}
                              {r.description && <p className="text-xs text-gray-500">{r.description}</p>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
      {scenes.length === 0 && <p className="p-4 text-gray-500 text-sm">尚無場景資料</p>}
    </div>
  );
}
